import { useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useTransactionData } from "@/context/transactionContext";
import { registerTransactionCount } from "@/services/api";

const isValidUrl = (urlString) => {
  if (!urlString) return false;
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

function useRedirect() {
  const history = useHistory();
  const { id, token } = useParams();
  const { transactionData } = useTransactionData();
  let pendingKycURL = null;

  const kycRequired = transactionData?.kyc_required || null;
  if (kycRequired) {
    pendingKycURL = transactionData?.customer?.pending_kyc_url;
  }
  const acceptedTerms = Boolean(transactionData?.customer?.terms_accepted_at);

  const checkDataAndRedirect = useCallback(() => {
    const isWithdrawalPending =
      transactionData?.type === "withdrawal" &&
      transactionData?.status === "pending" &&
      !transactionData?.customer_approval &&
      !transactionData?.bank_payment_data?.paymentDataValidated;

    const isWithdrawalInProcess =
      transactionData?.type === "withdrawal" &&
      transactionData?.customer_approval;

    const isDepositAlreadyRequested =
      transactionData?.type === "deposit" &&
      transactionData?.status !== "pending";

    if (
      pendingKycURL &&
      transactionData?.type === "deposit" &&
      transactionData?.status === "pending"
    ) {
      const validSuccessUrl = isValidUrl(transactionData?.redirect_url_success)
        ? transactionData.redirect_url_success
        : null;

      const validFailedUrl = isValidUrl(transactionData?.redirect_url_failed)
        ? transactionData.redirect_url_failed
        : null;

      return history.push(pendingKycURL, {
        merchant: transactionData?.merchant,
        transactionId: id,
        kycRequired,
        redirect_urls: {
          success: validSuccessUrl,
          failed: validFailedUrl,
        },
      });
    }

    if (isWithdrawalPending) {
      return history.push(
        `/payment-data/${id}/${token}${history.location.search}`,
      );
    }

    if (
      transactionData?.type === "deposit" &&
      transactionData?.bank_payment_data &&
      acceptedTerms
    ) {
      registerTransactionCount(id, token).catch((err) =>
        console.warn("Error updating transaction count:", err),
      );

      const currentSearch = history.location.search;
      const separator = currentSearch.includes("?") ? "&" : "?";
      return history.push(
        `/transaction-details/${id}/${token}${currentSearch}${separator}source=merchant`,
        {
          kycRequired,
        },
      );
    }

    if (isWithdrawalInProcess || isDepositAlreadyRequested) {
      return history.push(
        `/transaction-status/${id}/${token}${history.location.search}`,
      );
    }
    return null;
  }, [
    history,
    id,
    token,
    transactionData,
    pendingKycURL,
    acceptedTerms,
    kycRequired,
  ]);

  return { checkDataAndRedirect };
}

export default useRedirect;
